import React, { useEffect, useState } from 'react'
import {
  Step,
  StepConnector,
  type StepIconProps,
  Stepper,
  stepConnectorClasses,
} from '@mui/material'
import { styled } from '@mui/material/styles'

import { lightTheme } from '~createTheme'

const CustomStepper = styled(Stepper)({
  maxWidth: '300px',
  margin: '0 auto',
  '& .MuiStepConnector-root': {
    top: 10,
    left: 'calc(-50% + 10px)', // Adjust based on your dot size
    right: 'calc(50% + 10px)', // Adjust based on your dot size
  },
  '& .MuiStep-root': {
    padding: 0,
  },
})

const CustomConnector = styled(StepConnector)(({}) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: lightTheme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: lightTheme.palette.primary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: lightTheme.palette.action.selected,
    borderTopWidth: 3,
    borderRadius: 1,
  },
}))

interface CustomStepIconRootProps {
  ownerState: {
    active?: boolean
    completed?: boolean
  }
}

const CustomStepIconRoot = styled('div')<CustomStepIconRootProps>(({ ownerState }) => ({
  color: lightTheme.palette.action.selected,
  display: 'flex',
  height: 22,
  alignItems: 'center',
  justifyContent: 'center',
  ...(ownerState.active && {
    color: lightTheme.palette.primary.main,
  }),
  ...(ownerState.completed && {
    color: lightTheme.palette.primary.main,
  }),
  '& .CustomStepIcon-circle': {
    width: 20,
    height: 20,
    borderRadius: '50%',
    backgroundColor: 'currentColor',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: lightTheme.palette.primary.light,
    },
  },
}))

interface CustomStepIconProps extends StepIconProps {
  onClick: () => void
  completed: boolean
}

const CustomStepIcon: React.FC<CustomStepIconProps> = (props) => {
  const { active, completed, className, onClick } = props

  return (
    <CustomStepIconRoot ownerState={{ active, completed }} className={className}>
      <div className='CustomStepIcon-circle' onClick={onClick} />
    </CustomStepIconRoot>
  )
}

interface CustomDotStepperProps {
  steps: number
  initialActiveStep?: number
  activeStep?: number
  onStepChange?: (step: number) => void
}

const CustomDotStepper: React.FC<CustomDotStepperProps> = ({
  steps,
  initialActiveStep = 0,
  activeStep: externalActiveStep,
  onStepChange,
}) => {
  const [internalActiveStep, setInternalActiveStep] = useState(initialActiveStep)

  const activeStep = externalActiveStep !== undefined ? externalActiveStep : internalActiveStep

  useEffect(() => {
    if (externalActiveStep !== undefined) {
      setInternalActiveStep(externalActiveStep)
    }
  }, [externalActiveStep])

  const handleStepClick = (step: number): void => {
    if (onStepChange) {
      onStepChange(step)
    } else {
      setInternalActiveStep(step)
    }
  }

  return (
    <CustomStepper alternativeLabel activeStep={activeStep} connector={<CustomConnector />}>
      {[...Array(steps)].map((_, index) => (
        <Step key={index}>
          <CustomStepIcon
            active={index === activeStep}
            completed={index <= activeStep}
            onClick={() => handleStepClick(index)}
            icon={<></>}
          />
        </Step>
      ))}
    </CustomStepper>
  )
}

export default CustomDotStepper
